import { useState } from 'react'
import { AlertCircle, Copy, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { LisStage } from '@/api/lisStagesApi'
import { LisStageTestForm } from '@/components/admin/LisStageTestForm'
import {
  emptyTestSetup,
  providerSummary,
  testSetupBody,
  testSetupFromTest,
} from '@/lib/lis/orderingProvider'
import {
  useAddStagePredecessor,
  useAddStageTestCode,
  useCopyStageTestCodes,
  useCreateLisStage,
  useLisStages,
  useRemoveLisStage,
  useRemoveStagePredecessor,
  useRemoveStageTestCode,
  useUpdateLisStage,
  useUpdateStageTestCode,
} from '@/hooks/useLisStages'

/**
 * Configure → LIS Stages.
 *
 * A tenant defines its own stages here; nothing is hard-coded. Each stage says
 * which tests it orders and which stages must have results in before it can be
 * ordered at all.
 *
 * The predecessor rule is enforcement, not decoration: a stage whose
 * predecessors have no results yet cannot be selected on the ordering screen.
 * That is why the editor explains what it will do rather than leaving the
 * column to be inferred.
 */
export function LisStagesSettings() {
  const stages = useLisStages()
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const rows = stages.data ?? []
  const selected = rows.find((s) => s.id === selectedId) ?? rows[0] ?? null

  return (
    <div className='grid gap-6 lg:grid-cols-[320px_1fr]'>
      <StageList
        stages={rows}
        isLoading={stages.isPending}
        selectedId={selected?.id ?? null}
        onSelect={setSelectedId}
      />
      {selected ? (
        <StageEditor stage={selected} allStages={rows} />
      ) : (
        <p className='rounded-xl border bg-white p-6 text-sm text-muted-foreground shadow-sm'>
          Add a stage to begin. A stage is a point in the run that laboratory tests are ordered at —
          whatever your process calls them.
        </p>
      )}
    </div>
  )
}

function StageList({
  stages,
  isLoading,
  selectedId,
  onSelect,
}: {
  stages: LisStage[]
  isLoading: boolean
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  const [name, setName] = useState('')
  const create = useCreateLisStage()
  const update = useUpdateLisStage()
  const remove = useRemoveLisStage()

  const add = async () => {
    if (!name.trim()) return
    await create.mutateAsync({ name: name.trim(), sortOrder: stages.length })
    setName('')
  }

  return (
    <div className='rounded-xl border bg-white shadow-sm'>
      <div className='border-b px-4 py-3'>
        <h3 className='text-sm font-medium'>Stages</h3>
        <p className='mt-1 text-xs text-muted-foreground'>
          Shown on the ordering screen in this order.
        </p>
      </div>

      <div className='space-y-2 border-b p-3'>
        <Label htmlFor='lis-stage-name' className='text-xs'>
          Add a stage
        </Label>
        <div className='flex gap-2'>
          <Input
            id='lis-stage-name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='e.g. Stage-1'
          />
          <Button size='sm' onClick={add} disabled={!name.trim() || create.isPending}>
            {create.isPending ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <Plus className='h-4 w-4' />
            )}
          </Button>
        </div>
        {create.error ? <p className='text-xs text-destructive'>{create.error.message}</p> : null}
      </div>

      {isLoading ? (
        <p className='p-4 text-sm text-muted-foreground'>Loading…</p>
      ) : stages.length === 0 ? (
        <p className='p-4 text-sm text-muted-foreground'>No stages configured yet.</p>
      ) : (
        <ul>
          {stages.map((stage) => (
            <li key={stage.id} className='border-b last:border-0'>
              <div
                className={cn(
                  'flex items-center gap-2 px-3 py-2',
                  stage.id === selectedId && 'bg-muted/50',
                )}
              >
                <button type='button' className='flex-1 text-left' onClick={() => onSelect(stage.id)}>
                  <span className='text-sm font-medium'>{stage.name}</span>
                  <span className='ml-2 text-xs text-muted-foreground'>
                    {stage.testCodes.length} test{stage.testCodes.length === 1 ? '' : 's'}
                    {stage.predecessors.length > 0
                      ? ` · needs ${stage.predecessors.map((p) => p.name).join(', ')}`
                      : ''}
                  </span>
                </button>
                <button
                  type='button'
                  title={stage.active ? 'Disable this stage' : 'Enable this stage'}
                  onClick={() => update.mutate({ id: stage.id, active: !stage.active })}
                >
                  <Badge variant={stage.active ? 'default' : 'secondary'} className='text-[10px]'>
                    {stage.active ? 'Active' : 'Off'}
                  </Badge>
                </button>
                <button
                  type='button'
                  title='Remove this stage. Orders already placed keep the stage name they were sent with.'
                  className='text-muted-foreground hover:text-destructive'
                  onClick={() => remove.mutate(stage.id)}
                >
                  <Trash2 className='h-4 w-4' />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function StageEditor({ stage, allStages }: { stage: LisStage; allStages: LisStage[] }) {
  const update = useUpdateLisStage()
  const addPredecessor = useAddStagePredecessor()
  const removePredecessor = useRemoveStagePredecessor()
  const addTestCode = useAddStageTestCode()
  const updateTestCode = useUpdateStageTestCode()
  const removeTestCode = useRemoveStageTestCode()
  const copy = useCopyStageTestCodes()

  const [description, setDescription] = useState(stage.description ?? '')
  // One form open at a time: 'new' adds a test, an id edits that test.
  const [editing, setEditing] = useState<'new' | string | null>(null)
  const [predecessorId, setPredecessorId] = useState('')
  const [copyFrom, setCopyFrom] = useState('')

  const already = new Set(stage.predecessors.map((p) => p.id))
  const candidates = allStages.filter((s) => s.id !== stage.id && !already.has(s.id))
  const others = allStages.filter((s) => s.id !== stage.id)

  return (
    <div className='space-y-4'>
      <div className='space-y-3 rounded-xl border bg-white p-4 shadow-sm'>
        <h3 className='text-sm font-medium'>{stage.name}</h3>
        <div className='space-y-1.5'>
          <Label htmlFor='lis-stage-desc' className='text-xs'>
            Description
          </Label>
          <Input
            id='lis-stage-desc'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => {
              if (description !== (stage.description ?? '')) {
                update.mutate({ id: stage.id, description: description || null })
              }
            }}
            placeholder='What this stage is for'
          />
          {/* On MedStar a test name was typed here, for want of anywhere else
              to put it; this box is never sent to Epic (z8nrz7ffvz). */}
          <p className='text-[11px] text-muted-foreground'>
            Describes the stage, and is never sent to Epic. Each test has its own description below.
          </p>
        </div>
      </div>

      {/* ── Predecessors ───────────────────────────────────────────────── */}
      <div className='space-y-3 rounded-xl border bg-white p-4 shadow-sm'>
        <div>
          <h4 className='text-sm font-medium'>Needs results from</h4>
          <p className='mt-1 text-xs text-muted-foreground'>
            This stage cannot be ordered for a product until every stage listed here has results
            back for that product. An order that has been sent but not answered does not count.
          </p>
        </div>

        {stage.predecessors.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            Nothing — this stage can be ordered at any time.
          </p>
        ) : (
          <ul className='flex flex-wrap gap-2'>
            {stage.predecessors.map((p) => (
              <li key={p.id}>
                <button
                  type='button'
                  title='Remove this requirement'
                  className='flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-2.5 py-1 text-xs hover:border-destructive/50 hover:text-destructive'
                  onClick={() => removePredecessor.mutate({ id: stage.id, predecessorId: p.id })}
                >
                  {p.name}
                  <Trash2 className='h-3 w-3' />
                </button>
              </li>
            ))}
          </ul>
        )}

        {candidates.length > 0 ? (
          <div className='flex gap-2'>
            <Select value={predecessorId} onValueChange={setPredecessorId}>
              <SelectTrigger className='max-w-xs'>
                <SelectValue placeholder='Add a required stage…' />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size='sm'
              variant='outline'
              disabled={!predecessorId || addPredecessor.isPending}
              onClick={async () => {
                await addPredecessor.mutateAsync({ id: stage.id, predecessorId })
                setPredecessorId('')
              }}
            >
              Add
            </Button>
          </div>
        ) : null}

        {addPredecessor.error ? (
          <p className='flex items-start gap-2 text-sm text-destructive'>
            <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
            {addPredecessor.error.message}
          </p>
        ) : null}
      </div>

      {/* ── Test codes ─────────────────────────────────────────────────── */}
      <div className='space-y-3 rounded-xl border bg-white p-4 shadow-sm'>
        <div>
          <h4 className='text-sm font-medium'>Tests ordered at this stage</h4>
          <p className='mt-1 text-xs text-muted-foreground'>
            Ticked ones are pre-selected when the stage is chosen. The operator can untick any of
            them, and add a code that is not listed. Each test has a code, a description, and where
            its ordering provider comes from.
          </p>
        </div>

        {stage.testCodes.length === 0 ? (
          <p className='text-sm text-muted-foreground'>No tests yet.</p>
        ) : (
          <ul className='space-y-1'>
            {stage.testCodes.map((t) =>
              editing === t.id ? (
                <li key={t.id}>
                  <LisStageTestForm
                    initial={testSetupFromTest(t)}
                    codeLocked
                    submitLabel='Save test'
                    pending={updateTestCode.isPending}
                    serverError={updateTestCode.error?.message}
                    onCancel={() => {
                      updateTestCode.reset()
                      setEditing(null)
                    }}
                    onSubmit={async (draft) => {
                      // The code is the test's identity and is not renamed here.
                      const { testCode: _code, ...body } = testSetupBody(draft)
                      await updateTestCode.mutateAsync({ testCodeId: t.id, ...body })
                      setEditing(null)
                    }}
                  />
                </li>
              ) : (
                <li key={t.id} className='flex items-start gap-3 rounded-lg border px-3 py-2'>
                  <input
                    type='checkbox'
                    className='mt-1'
                    title='Pre-selected when the stage is chosen'
                    checked={t.defaultSelected}
                    // It shares the edit form's mutation: toggled while a form is
                    // open, the form would show its spinner and its errors.
                    disabled={editing !== null}
                    onChange={(e) =>
                      updateTestCode.mutate({
                        testCodeId: t.id,
                        defaultSelected: e.target.checked,
                      })
                    }
                  />
                  <div className='min-w-0 flex-1 text-sm'>
                    <p>
                      <span className='font-mono'>{t.testCode}</span>
                      {t.description ? <span className='ml-2'>{t.description}</span> : null}
                      <span className='ml-2 text-xs text-muted-foreground'>
                        {t.defaultSelected ? 'pre-selected' : 'offered, not pre-selected'}
                      </span>
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      {providerSummary(t)}
                      {t.providerSource && t.blockIfNoProvider ? ' · blocks the order if none' : ''}
                    </p>
                  </div>
                  <button
                    type='button'
                    title='Edit this test'
                    className='text-muted-foreground hover:text-foreground disabled:opacity-40'
                    disabled={editing !== null}
                    onClick={() => {
                      // Clear an earlier toggle's error, or the form opens showing it.
                      updateTestCode.reset()
                      setEditing(t.id)
                    }}
                  >
                    <Pencil className='h-4 w-4' />
                  </button>
                  <button
                    type='button'
                    title='Remove this test from the stage'
                    className='text-muted-foreground hover:text-destructive'
                    onClick={() => removeTestCode.mutate(t.id)}
                  >
                    <Trash2 className='h-4 w-4' />
                  </button>
                </li>
              ),
            )}
          </ul>
        )}

        {editing === 'new' ? (
          <LisStageTestForm
            initial={emptyTestSetup()}
            submitLabel='Add test'
            pending={addTestCode.isPending}
            serverError={addTestCode.error?.message}
            onCancel={() => {
              addTestCode.reset()
              setEditing(null)
            }}
            onSubmit={async (draft) => {
              await addTestCode.mutateAsync({ id: stage.id, ...testSetupBody(draft) })
              setEditing(null)
            }}
          />
        ) : (
          <Button size='sm' variant='outline' disabled={editing !== null} onClick={() => setEditing('new')}>
            <Plus className='mr-1 h-4 w-4' />
            Add test
          </Button>
        )}

        {others.length > 0 ? (
          <div className='flex items-center gap-2 border-t pt-3'>
            {/* Pre and Post overlap heavily; retyping a dozen codes is how a
                typo gets in. Codes already here are skipped, not duplicated. */}
            <Select value={copyFrom} onValueChange={setCopyFrom}>
              <SelectTrigger className='max-w-xs'>
                <SelectValue placeholder='Copy tests from another stage…' />
              </SelectTrigger>
              <SelectContent>
                {others.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} ({s.testCodes.length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size='sm'
              variant='outline'
              disabled={!copyFrom || copy.isPending}
              onClick={async () => {
                await copy.mutateAsync({ toStageId: stage.id, fromStageId: copyFrom })
                setCopyFrom('')
              }}
            >
              <Copy className='mr-1 h-4 w-4' />
              Copy
            </Button>
            {copy.data ? (
              <span className='text-xs text-muted-foreground'>
                Copied {copy.data.copied}
                {copy.data.skipped > 0 ? `, ${copy.data.skipped} already here` : ''}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}
